import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D10fromD1 = () => {
  const title = "Calculation of D10 (Dasamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D10 chart, or Dasamsa chart, is a significant divisional chart in
        Vedic astrology. It provides insights into career, profession, and one's
        reputation in society. Below is a step-by-step procedure to calculate
        the D10 chart from the D1 chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D10 (Dasamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, note the degree of the planet in the D1 chart. For example, if
        Mars is located at 14° Aries, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Degree by 3 to Find the Dasamsa Position
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into 10 equal parts, called Dasamsas, with
        each Dasamsa spanning 3°. To find which Dasamsa a planet occupies, we
        divide the planet's degree within the sign by 3.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Dasamsa \ Position = \frac{Degree \ in \ Sign}{3}`}
        />
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Determine the Integer Part
      </Typography>

      <Typography variant="body1" paragraph>
        The result from the division gives us the Dasamsa position. Ignore the
        decimal part and take the integer value. For instance, if the result is
        4.67, we take 4 as the Dasamsa position.
      </Typography>

      <Typography variant="h5" gutterBottom>
        4. Assign the Dasamsa Sign
      </Typography>

      <Typography variant="body1" paragraph>
        The 10 Dasamsas within each sign follow a specific sequence based on the
        element of the sign (Fire, Earth, Air, or Water).
      </Typography>

      <Box sx={{ margin: "1rem 0" }}>
        <Typography variant="h6" gutterBottom>
          - Fire Signs : Aries, Leo, Sagittarius
        </Typography>
        <Typography variant="body2" paragraph>
          Dasamsa Sequence: Aries, Capricorn, Libra, Cancer, Aries, Capricorn,
          Libra, Cancer, Aries, Capricorn.
        </Typography>

        <Typography variant="h6" gutterBottom>
          - Earth Signs : Taurus, Virgo, Capricorn
        </Typography>
        <Typography variant="body2" paragraph>
          Dasamsa Sequence: Capricorn, Libra, Cancer, Aries, Capricorn, Libra,
          Cancer, Aries, Capricorn, Libra.
        </Typography>

        <Typography variant="h6" gutterBottom>
          - Air Signs : Gemini, Libra, Aquarius
        </Typography>
        <Typography variant="body2" paragraph>
          Dasamsa Sequence: Libra, Cancer, Aries, Capricorn, Libra, Cancer,
          Aries, Capricorn, Libra, Cancer.
        </Typography>

        <Typography variant="h6" gutterBottom>
          - Water Signs : Cancer, Scorpio, Pisces
        </Typography>
        <Typography variant="body2" paragraph>
          Dasamsa Sequence: Cancer, Aries, Capricorn, Libra, Cancer, Aries,
          Capricorn, Libra, Cancer, Aries.
        </Typography>
      </Box>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D10 (Dasamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D10
        (Dasamsa) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_dasamsa(degree, sign):
    fire_signs = ['Aries', 'Leo', 'Sagittarius']
    earth_signs = ['Taurus', 'Virgo', 'Capricorn']
    air_signs = ['Gemini', 'Libra', 'Aquarius']
    water_signs = ['Cancer', 'Scorpio', 'Pisces']

    fire_sequence = ['Aries', 'Capricorn', 'Libra', 'Cancer', 'Aries', 'Capricorn', 'Libra', 'Cancer', 'Aries', 'Capricorn']
    earth_sequence = ['Capricorn', 'Libra', 'Cancer', 'Aries', 'Capricorn', 'Libra', 'Cancer', 'Aries', 'Capricorn', 'Libra']
    air_sequence = ['Libra', 'Cancer', 'Aries', 'Capricorn', 'Libra', 'Cancer', 'Aries', 'Capricorn', 'Libra', 'Cancer']
    water_sequence = ['Cancer', 'Aries', 'Capricorn', 'Libra', 'Cancer', 'Aries', 'Capricorn', 'Libra', 'Cancer', 'Aries']

    dasamsa_position = degree / 3
    dasamsa_index = int(dasamsa_position)

    if sign in fire_signs:
        dasamsa_sign = fire_sequence[dasamsa_index]
    elif sign in earth_signs:
        dasamsa_sign = earth_sequence[dasamsa_index]
    elif sign in air_signs:
        dasamsa_sign = air_sequence[dasamsa_index]
    elif sign in water_signs:
        dasamsa_sign = water_sequence[dasamsa_index]
    else:
        return "Invalid Sign"

    return dasamsa_sign

# Example usage
planet = "Mars"
degree = 14
sign = "Aries"

dasamsa_sign = calculate_dasamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {dasamsa_sign} in the D10 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program uses the planet's degree in the D1 chart and
        the sign it occupies to calculate the Dasamsa sign. It follows the same
        steps we have already covered manually.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the planet's degree by 3 to find the Dasamsa
        position. It then takes the integer part of the result to identify which
        Dasamsa the planet falls into. Based on the element of the sign (Fire,
        Earth, Air, Water), the correct Dasamsa sequence is chosen, and the
        Dasamsa sign is returned.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D10fromD1;
